import { ShepherdBase } from 'shepherd.js';
import { offset, flip, shift, detectOverflow } from '@floating-ui/dom';

/**
 * Retrieves the list of tours that have been shown from localStorage.
 * @returns {string[]} An array of tour IDs that have been shown.
 */

const getShownTours = () => JSON.parse(localStorage.getItem('shownTours')) || [];

/**
 * Checks if a specific tour has been shown.
 * @param {string} tourId - The ID of the tour to check.
 * @returns {boolean} True if the tour has been shown, false otherwise.
 */
const hasTourBeenShown = (tourId: string) => getShownTours().includes(tourId);

/**
 * Marks a specific tour as shown by adding it to localStorage.
 * @param {string} tourId - The ID of the tour to mark as shown.
 * @returns {void}
 */
const markTourAsShown = (tourId: string) => {
  const shownTours = getShownTours();
  if (!shownTours.includes(tourId)) {
    shownTours.push(tourId);
    localStorage.setItem('shownTours', JSON.stringify(shownTours));
  }
};

/**
 * The key a tour is remembered under.
 *
 * When the viewer is embedded the host app tells us who is logged in, so the
 * tour is remembered per user: one radiologist finishing it on a shared
 * workstation must not hide it from the next person to sign in. Standalone
 * OHIF has no user, and falls back to the plain tour id.
 *
 * @param {string} tourId - The ID of the tour.
 * @param {string|number} [userId] - The logged-in user, when known.
 * @returns {string} The localStorage key for that tour.
 */
const tourSeenKey = (tourId: string, userId?: string | number) =>
  userId === undefined || userId === null ? tourId : `${tourId}:${userId}`;

/**
 * Whether a tour should start now.
 *
 * `force` is how the host app replays a tour on request: it overrides both the
 * "already seen" record and the guard against starting twice.
 *
 * @param {string} tourId - The ID of the tour.
 * @param {object} options - userId, force, and whether this tour already started.
 * @returns {boolean} True when the tour should start.
 */
const shouldStartTour = (
  tourId: string,
  {
    userId,
    force,
    alreadyStarted,
  }: { userId?: string | number; force?: boolean; alreadyStarted?: boolean } = {}
) => {
  if (force) {
    return true;
  }
  if (alreadyStarted) {
    return false;
  }
  return !hasTourBeenShown(tourSeenKey(tourId, userId));
};

/**
 * Default handler for the 'show' event in Shepherd steps.
 * Adds a progress indicator to the footer of the current step.
 *
 * @param {ShepherdBase} Shepherd - The Shepherd.js instance.
 * @returns {void}
 */
const defaultShowHandler = (Shepherd: ShepherdBase) => {
  const currentStep = Shepherd.activeTour?.getCurrentStep();
  if (currentStep) {
    const progress = document.createElement('span');
    progress.className = 'shepherd-progress text-lg text-muted-foreground';
    progress.innerText = `${Shepherd.activeTour?.steps.indexOf(currentStep) + 1}/${Shepherd.activeTour?.steps.length}`;
    progress.style.position = 'absolute';
    progress.style.left = '13px';
    progress.style.bottom = '20px';
    progress.style.zIndex = '1';

    const footer = currentStep?.getElement()?.querySelector('.shepherd-footer');
    footer?.appendChild(progress);
  }
};

/**
 * Custom middleware for adjusting Shepherd step positioning when overflowing.
 *
 * @type {object}
 * @property {string} name - The name of the middleware.
 * @property {function} fn - The function that adjusts the position of the step when overflowing.
 */

const customMiddleware = {
  name: 'customOverflowMiddleware',
  async fn(state) {
    const overflow = await detectOverflow(state, {
      boundary: document.querySelector('body'),
      padding: 24,
    });

    const xAdjustment =
      overflow.left > 0 ? overflow.left : overflow.right > 0 ? -overflow.right : 0;
    const yAdjustment =
      overflow.top > 0 ? overflow.top : overflow.bottom > 0 ? -overflow.bottom : 0;

    return {
      x: state.x + xAdjustment,
      y: state.y + yAdjustment,
    };
  },
};

/**
 * Default Floating UI middleware for positioning steps in Shepherd.js.
 * Includes offset, shift, flip, and custom overflow middleware.
 *
 * @type {Array<object>}
 */

const middleware = [offset(15), shift(), flip(), customMiddleware];

export {
  hasTourBeenShown,
  markTourAsShown,
  tourSeenKey,
  shouldStartTour,
  middleware,
  defaultShowHandler,
};
